import type { CityConfig } from "../city-template/types";

export const greenbeltConfig: CityConfig = {
  city: "Greenbelt",
  state: "Maryland",
  stateAbbr: "MD",
  slug: "greenbelt",
  route: "/cities/greenbelt",
  title: "Emergency Passport Greenbelt MD | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Greenbelt, Maryland — as fast as 24 hours. Fast passport renewal and new passport applications for Greenbelt, College Park, Hyattsville, and Prince George's County. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Greenbelt, MD",
  heroSubtitle: "Greenbelt residents — from NASA Goddard researchers to University of Maryland families to Beltway commuters — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving Prince George's County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Greenbelt%20Maryland%20historic%20planned%20community%20with%20Art%20Deco%20architecture%2C%20tree-lined%20residential%20streets%2C%20community%20center%20building%2C%20NASA%20Goddard%20research%20campus%20in%20background%2C%20warm%20golden%20hour%20lighting%2C%20professional%20suburban%20photography%2C%20no%20visible%20text%20or%20people%2C%20green%20park%20spaces%20and%20mature%20trees&width=1800&height=700&seq=greenbelt-hero-001&orientation=landscape",
  heroImageAlt: "Historic planned community view of Greenbelt Maryland with Art Deco architecture and community park spaces",
  geoPosition: "39.0046;-76.8755",
  geoPlacename: "Greenbelt, Maryland",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Washington DC Passport Agency",
    address: "600 19th St NW, 1st Floor, Washington, DC 20006",
    distance: "Approximately 20–30 minutes from Greenbelt via I-495 S",
  },
  nearbyCitiesZips: [
    { name: "Greenbelt", zips: "20768, 20770, 20771" },
    { name: "College Park", zips: "20740, 20741, 20742" },
    { name: "Hyattsville", zips: "20781, 20782, 20783, 20784, 20785, 20787, 20788" },
    { name: "Lanham", zips: "20703, 20706" },
    { name: "Beltsville", zips: "20704, 20705" },
    { name: "Laurel", zips: "20707, 20708, 20709, 20723, 20725, 20726" },
    { name: "Riverdale", zips: "20737, 20738" },
    { name: "Berwyn Heights", zips: "20740" },
    { name: "New Carrollton", zips: "20784, 20787" },
    { name: "Bowie", zips: "20715, 20716, 20717, 20718, 20719, 20720, 20721" },
    { name: "Silver Spring", zips: "20901, 20902, 20903, 20904, 20905, 20906, 20910, 20911" },
    { name: "Takoma Park", zips: "20901, 20910, 20912, 20913" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Greenbelt and Prince George's County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-rocket-line", title: "NASA & Research Hub", desc: "Serving Goddard Space Flight Center scientists and visiting researchers" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown DC" },
  ],
  internalLinks: [
    { name: "Expedited passport in College Park, MD", route: "/cities/college-park" },
    { name: "Emergency passport help in Bowie, MD", route: "/cities/bowie" },
    { name: "Urgent passport services in Laurel, MD", route: "/cities/laurel" },
    { name: "Maryland passport services overview", route: "/states/maryland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Greenbelt, Maryland remotely via secure registered courier. We do not have a walk-in office in Greenbelt — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Greenbelt travelers without the drive to downtown DC.",
  fpcOfficeDistance: "Serving Greenbelt, College Park, Hyattsville, Lanham, and greater Prince George's County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Greenbelt area and Prince George's County. From the historic planned community near the original Roosevelt-era town center to the NASA Goddard Space Flight Center campus, from the bustling University of Maryland corridor to the residential neighborhoods along the Beltway — we serve all of Greenbelt's diverse communities.",
  nearbyCitiesIntro: "We serve Greenbelt and nearby communities including College Park, Hyattsville, Lanham, Beltsville, Laurel, Riverdale, Berwyn Heights, New Carrollton, Bowie, and Takoma Park. We also serve Montgomery County residents in Silver Spring.",
  howItWorksIntro: "Most Greenbelt travelers complete everything online and ship documents overnight. No need to drive 20–30 minutes to downtown DC or navigate the Beltway during rush hour.",
  mistakesIntro: "Greenbelt is a unique community with a mix of NASA researchers, university students, and Beltway commuters. Here are the most common passport mistakes we see from Greenbelt travelers.",
  testimonialsIntro: "Greenbelt travelers — from Goddard scientists to UMD families to commuters — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Greenbelt, MD? Here are the most common ones we hear from Prince George's County travelers.",
  statePageRoute: "/states/maryland",
  statePageName: "Maryland",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 20–30 min to downtown DC", "Full day commitment — Beltway traffic can be brutal"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for NASA and research travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Greenbelt travelers with immediate departure needs — as fast as 24 hours. Perfect for NASA conferences, research trips, and last-minute international travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "NASA and research travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Greenbelt adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Greenbelt address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Greenbelt residents and families — including university students and visiting researchers.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Greenbelt residents who lost their passport before travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Greenbelt families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Greenbelt residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Greenbelt MD", description: "Urgent passport processing for Greenbelt Maryland travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Greenbelt MD", description: "Fast passport renewal for Greenbelt Maryland residents with a previously-issued passport." },
    { name: "First-Time Passport Greenbelt MD", description: "New passport application services for Greenbelt Maryland residents applying for their first passport." },
    { name: "Child Passport Greenbelt MD", description: "Minor passport services for Greenbelt Maryland families with children under 16." },
  ],
  testimonials: [
    {
      name: "Dr. Patel R.",
      location: "NASA Goddard, Greenbelt",
      rating: 5,
      text: "Conference in Geneva in 3 days. Fast Passport Center reviewed every document, caught a minor form issue, and delivered my passport in 30 hours. Essential for scientists with tight conference schedules.",
      service: "Emergency Passport",
    },
    {
      name: "Karen L.",
      location: "College Park, MD",
      rating: 5,
      text: "Study abroad semester in Spain with 8 days notice. Fast Passport Center handled everything — we shipped from the Greenbelt FedEx and had my passport in 4 days. Perfect for student travel.",
      service: "First-Time Passport",
    },
    {
      name: "Marcus W.",
      location: "Hyattsville, MD",
      rating: 5,
      text: "Business trip to London with 6 days notice. Fast Passport Center had my renewed passport ready in 2 days. No need to drive to DC — everything shipped from the UPS Store on Greenbelt Rd.",
      service: "Passport Renewal",
    },
    {
      name: "Sandra M.",
      location: "Greenbelt, MD",
      rating: 5,
      text: "Family trip to Jamaica with 12 days notice. Four passports processed in under a week. The agent was incredibly responsive and kept us updated every day.",
      service: "Family Passport Bundle",
    },
  ],
  facilities: [
    {
      id: "greenbelt-1",
      name: "Greenbelt Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7600 Ora Glen Dr",
      city: "Greenbelt",
      state: "MD",
      zip: "20770",
      phone: "(301) 474-4341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Greenbelt, near Greenbelt Metro Station",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Greenbelt acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient Metro access via Greenbelt Station on the Green Line.",
      icon: "ri-mail-line",
    },
    {
      id: "greenbelt-2",
      name: "College Park Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4315 Knox Rd",
      city: "College Park",
      state: "MD",
      zip: "20740",
      phone: "(301) 699-6132",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "College Park, approximately 2 miles south of Greenbelt",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for University of Maryland students and College Park residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "greenbelt-3",
      name: "Hyattsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4325 Gallatin St",
      city: "Hyattsville",
      state: "MD",
      zip: "20781",
      phone: "(301) 779-0010",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Hyattsville, approximately 4 miles southwest of Greenbelt",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Hyattsville and western Prince George's County residents. Good alternative when Greenbelt appointments are full.",
      icon: "ri-mail-line",
    },
    {
      id: "greenbelt-4",
      name: "Laurel Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "431 Main St",
      city: "Laurel",
      state: "MD",
      zip: "20707",
      phone: "(301) 776-5000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Laurel, approximately 6 miles northeast of Greenbelt",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serving Laurel and eastern Prince George's County residents. Ample parking available.",
      icon: "ri-mail-line",
    },
    {
      id: "greenbelt-5",
      name: "Prince George's County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "14735 Main St",
      city: "Upper Marlboro",
      state: "MD",
      zip: "20772",
      phone: "(301) 952-3318",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Upper Marlboro, approximately 15 miles southeast of Greenbelt",
      website: "https://www.princegeorgescountymd.gov/",
      websiteLabel: "Prince George's County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to post office locations. Serving all Prince George's County residents.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited DC slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 20–30 min drive to downtown DC", fpc: "No — ship from Greenbelt" },
    { feature: "Research Travel Support", agency: "No flexibility for academic needs", fpc: "Yes — we handle NASA and academic cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Greenbelt's busy researchers and students often discover passport needs just weeks before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services in Greenbelt sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Greenbelt and Prince George's County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking the Acceptance Facility Early",
      description: "Greenbelt Post Office appointments fill up fast, especially during summer and holiday travel seasons. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Greenbelt residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Greenbelt, College Park, or Hyattsville." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx location near Greenbelt Rd or the University of Maryland." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Greenbelt address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Greenbelt, MD?", a: "Fast Passport Center offers emergency passport processing in Greenbelt, Maryland in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Washington DC Passport Agency (20–30 minutes from Greenbelt) — without you needing to make the drive or fight Beltway traffic." },
    { q: "Does Greenbelt have its own passport agency?", a: "No — neither Greenbelt nor the state of Maryland has a regional passport agency. The closest facility is the Washington DC Passport Agency, approximately 20–30 minutes from Greenbelt via I-495 S. Fast Passport Center's courier service means Greenbelt residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in College Park or Hyattsville?", a: "Absolutely. Fast Passport Center serves all of the Greenbelt area including College Park, Hyattsville, Lanham, Beltsville, and Laurel. We also serve Bowie and Takoma Park residents. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Greenbelt, MD?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Greenbelt travelers use for international flights?", a: "Most Greenbelt residents fly internationally through Dulles International Airport (IAD), approximately 35–45 minutes west via I-495 W. Reagan National (DCA) is 25–35 minutes south and offers domestic connections. Baltimore-Washington International (BWI) is 25–35 minutes northeast. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Greenbelt?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Greenbelt, College Park, Hyattsville, and surrounding areas. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to Greenbelt?", a: "The Greenbelt Post Office at 7600 Ora Glen Dr is the primary downtown acceptance facility. The College Park Post Office at 4315 Knox Rd serves UMD students. The Hyattsville Post Office at 4325 Gallatin St serves western residents. All require appointments — book 1–2 weeks ahead." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Greenbelt residents should plan ahead, especially for NASA conferences and academic travel." },
  ],
  photoLocations: [
    { name: "Walgreens — Greenbelt", address: "7599 Greenbelt Rd, Greenbelt, MD 20770", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Greenbelt", address: "7415 Greenbelt Rd, Greenbelt, MD 20770", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Greenbelt", address: "7700 Greenbelt Rd, Greenbelt, MD 20770", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — College Park", address: "7423 Baltimore Ave, College Park, MD 20740", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "MVA — Beltsville", address: "11701 Baltimore Ave, Beltsville, MD 20705", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Prince George's County and northern DC metro. REAL ID processing available.", featured: true },
    { name: "MVA — Largo", address: "1025 Largo Center Dr, Upper Marlboro, MD 20774", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Prince George's County and southern Maryland. Full service including REAL ID.", featured: false },
  ],
  airports: [
    {
      code: "IAD",
      name: "Dulles International",
      distance: "35–45 minutes from Greenbelt via I-495 W",
      address: "1 Saarinen Cir, Dulles, VA 20166",
      note: "Primary international departure airport for Greenbelt travelers. Direct flights to London, Frankfurt, Tokyo, and major European hubs. United Airlines hub.",
      flightUrl: "https://www.flydulles.com/",
      flightLabel: "IAD Flight Info",
      featured: true,
    },
    {
      code: "DCA",
      name: "Reagan National (Washington DC)",
      distance: "25–35 minutes from Greenbelt via I-495 S",
      address: "2401 Smith Blvd, Arlington, VA 22202",
      note: "Closest airport for domestic connections. Limited international flights but excellent for connecting to major hubs.",
      flightUrl: "https://www.flyreagan.com/",
      flightLabel: "DCA Flight Info",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International Thurgood Marshall",
      distance: "25–35 minutes from Greenbelt via I-495 E",
      address: "7050 Friendship Rd, Baltimore, MD 21240",
      note: "Alternative for Southwest Airlines domestic connections and some Caribbean destinations. Convenient for Prince George's County residents.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Flight Info",
      featured: false,
    },
  ],
};